import type { ApiRfiDetail, ApiRfiListItem, ApiRfiSearchResult } from "../../shared/interfaces";
import type { ProjectTransport } from "./transport";
import {
    failValidation,
    optionalField,
    requireArray,
    requireBoolean,
    requireNonNegativeInteger,
    requireNullableString,
    requireNullableWireString,
    requireOptionalNullableWireString,
    requireOptionalWireString,
    requireRecord,
    requireString,
    requireWireString,
    requireWireStringArray,
    validateResponseListRows,
} from "./validation";

const RFIS_ENDPOINT = "/api/ai/rfis";

export interface RfiListPage {
    items: ApiRfiListItem[];
    total: number;
}

function validateRfiIdentity(
    value: unknown,
    endpoint: string,
    field: string,
): Record<string, unknown> {
    const row = requireRecord(value, endpoint, field);
    requireString(row.id, endpoint, `${field}.id`);
    requireNullableString(row.documentId, endpoint, `${field}.documentId`);
    requireNullableWireString(row.rfiNumber, endpoint, `${field}.rfiNumber`);
    requireWireString(row.title, endpoint, `${field}.title`);
    requireNullableWireString(row.status, endpoint, `${field}.status`);
    return row;
}

function validateRfiListItem(value: unknown, endpoint: string, field: string): ApiRfiListItem {
    const row = validateRfiIdentity(value, endpoint, field);
    requireNullableWireString(row.discipline, endpoint, `${field}.discipline`);
    requireNullableWireString(row.dueDate, endpoint, `${field}.dueDate`);
    requireNullableWireString(row.updatedAt, endpoint, `${field}.updatedAt`);
    requireNullableWireString(row.priority, endpoint, `${field}.priority`);
    requireWireString(row.source, endpoint, `${field}.source`);
    requireNullableWireString(row.externalUrl, endpoint, `${field}.externalUrl`);

    const impacted = requireRecord(row.impacted, endpoint, `${field}.impacted`);
    requireNonNegativeInteger(impacted.count, endpoint, `${field}.impacted.count`);
    requireWireStringArray(impacted.codes, endpoint, `${field}.impacted.codes`);

    const review = requireRecord(row.review, endpoint, `${field}.review`);
    requireOptionalNullableWireString(
        optionalField(review, "status"),
        endpoint,
        `${field}.review.status`,
    );
    requireOptionalWireString(
        optionalField(review, "evidence"),
        endpoint,
        `${field}.review.evidence`,
    );
    requireNonNegativeInteger(review.pending, endpoint, `${field}.review.pending`);
    requireNonNegativeInteger(review.approved, endpoint, `${field}.review.approved`);
    requireNonNegativeInteger(review.total, endpoint, `${field}.review.total`);
    requireNullableWireString(review.reviewedBy, endpoint, `${field}.review.reviewedBy`);
    requireNullableWireString(review.reviewedByName, endpoint, `${field}.review.reviewedByName`);
    return row as unknown as ApiRfiListItem;
}

function validateRfiDetail(value: unknown, endpoint: string): ApiRfiDetail {
    const row = requireRecord(value, endpoint, "response");
    if (row.kind !== "rfi") return failValidation(endpoint, "kind");
    requireString(row.id, endpoint, "id");
    requireString(row.documentId, endpoint, "documentId");
    requireWireString(row.title, endpoint, "title");
    requireArray(row.attachments, endpoint, "attachments");
    requireArray(row.metadataFields, endpoint, "metadataFields");
    requireArray(row.contentSections, endpoint, "contentSections");
    requireBoolean(row.ingested, endpoint, "ingested");
    requireArray(row.referencedDrawings, endpoint, "referencedDrawings");
    requireArray(row.referencedSpecs, endpoint, "referencedSpecs");
    return row as unknown as ApiRfiDetail;
}

export interface ProjectRfiApi {
    listPage(offset: number, limit: number, signal?: AbortSignal): Promise<RfiListPage>;
    search(query: string, limit?: number, signal?: AbortSignal): Promise<ApiRfiSearchResult[]>;
    getByDocumentId(documentId: string, signal?: AbortSignal): Promise<ApiRfiDetail | null>;
}

export function createRfiApi(transport: ProjectTransport): ProjectRfiApi {
    return {
        async listPage(offset, limit, signal) {
            const response = await transport.request<unknown>({
                method: "GET",
                path: RFIS_ENDPOINT,
                query: { offset, limit },
                signal,
            });
            const payload = requireRecord(response, RFIS_ENDPOINT, "response");
            const rows = requireArray(payload.rfis, RFIS_ENDPOINT, "rfis");
            const items = validateResponseListRows(
                rows,
                RFIS_ENDPOINT,
                "RFI",
                (value, field) => validateRfiListItem(value, RFIS_ENDPOINT, field),
                undefined,
                "rfis",
            );
            return {
                items,
                total: requireNonNegativeInteger(payload.total, RFIS_ENDPOINT, "total"),
            };
        },

        async search(query, limit = 20, signal) {
            const path = `${RFIS_ENDPOINT}/search`;
            const response = await transport.request<unknown>({
                method: "GET",
                path,
                query: { q: query, limit },
                signal,
            });
            const payload = requireRecord(response, path, "response");
            const rows = requireArray(payload.rfis, path, "rfis");
            return validateResponseListRows(
                rows,
                path,
                "RFI",
                (row, field) =>
                    validateRfiIdentity(row, path, field) as unknown as ApiRfiSearchResult,
                undefined,
                "rfis",
            );
        },

        async getByDocumentId(documentId, signal) {
            const path = `/api/ai/document/${documentId}/rfi`;
            try {
                const response = await transport.request<unknown>({ method: "GET", path, signal });
                return validateRfiDetail(response, path);
            } catch (error) {
                if (transport.getErrorStatus(error) === 404) return null;
                throw error;
            }
        },
    };
}
